/**
 * PATH: packages/email-hub/src/feature/components/email-context-menu/DispositionActions.tsx
 * FILE_ID: EMAILHUB-CONTEXTMENU-DISPOSITION-ACTIONS-001
 * PURPOSE: Read, snooze, archive, junk, and bin actions.
 * WHY: Keeps mailbox-disposition behavior in one action group.
 */
import { Archive, Ban, Clock, Eye, RotateCcw, Trash2 } from 'lucide-react'

import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
} from '../../../_internal/components/ContextMenu'
import { formatResurfaceTime, snoozePresets } from '../../lib/resurfaceTimes'
import type { EmailContextMenuController } from './useEmailContextMenuController'

export function DispositionActions({
  controller,
}: {
  controller: EmailContextMenuController
}) {
  const {
    applyAutoJunk,
    archive,
    bulkAction,
    ctxMenu,
    onPatchLocal,
    onRefetch,
    onResolvedLocal,
    setCtxMenu,
    toast,
    toggleRead,
    unarchive,
  } = controller
  if (!ctxMenu) return null

  const email = ctxMenu.email
  const ids = ctxMenu.emailIds.length > 0 ? ctxMenu.emailIds : [email.id]
  const bulk = ids.length > 1
  const isSnoozed =
    Boolean(email.snoozed_until) &&
    new Date(email.snoozed_until as string).getTime() > Date.now()
  const address = (email.from_address || '').toLowerCase()
  const domain = address.includes('@') ? address.split('@')[1] : ''
  const close = () => setCtxMenu(null)

  return (
    <>
      <ContextMenuItem
        icon={Eye}
        onClick={async () => {
          const { id, is_read } = email
          close()
          if (bulk) {
            try {
              await bulkAction(ids, is_read ? 'mark_unread' : 'mark_read')
              onRefetch?.()
            } catch {
              onRefetch?.()
            }
            return
          }
          onPatchLocal?.(id, { is_read: !is_read })
          try {
            await toggleRead(id, is_read)
            onRefetch?.()
          } catch {
            onRefetch?.()
          }
        }}
      >
        Mark {bulk ? `${ids.length} ` : ''}as {email.is_read ? 'Unread' : 'Read'}
      </ContextMenuItem>
      {isSnoozed ? (
        <ContextMenuItem
          icon={Clock}
          onClick={async () => {
            close()
            onPatchLocal?.(email.id, { snoozed_until: null })
            try {
              await bulkAction([email.id], 'unsnooze')
              toast('Unsnoozed — back in the inbox.', 'success')
              onRefetch?.()
            } catch {
              onRefetch?.()
            }
          }}
        >
          Unsnooze
          <span className="ml-2 text-[10px] text-muted-foreground truncate">
            was {formatResurfaceTime(email.snoozed_until as string)}
          </span>
        </ContextMenuItem>
      ) : (
        <ContextMenuSub label="Snooze" icon={Clock}>
          {snoozePresets().map((preset) => (
            <ContextMenuItem
              key={preset.key}
              onClick={async () => {
                close()
                ids.forEach((id) => onResolvedLocal?.(id))
                try {
                  await bulkAction(ids, 'snooze', {
                    snoozed_until: preset.at.toISOString(),
                  })
                  toast(
                    `Snoozed until ${formatResurfaceTime(preset.at)}.`,
                    'success',
                  )
                  onRefetch?.()
                } catch {
                  onRefetch?.()
                }
              }}
            >
              {preset.label}
            </ContextMenuItem>
          ))}
        </ContextMenuSub>
      )}
      {email.status === 'binned' ? null : email.is_archived ? (
        <ContextMenuItem
          icon={RotateCcw}
          onClick={async () => {
            ids.forEach((id) => onResolvedLocal?.(id))
            try {
              if (bulk) await bulkAction(ids, 'unarchive')
              else await unarchive(email.id, { isRead: email.is_read })
              onRefetch?.()
            } catch {
              onRefetch?.()
            }
            close()
          }}
        >
          Unarchive
        </ContextMenuItem>
      ) : (
        <ContextMenuItem
          icon={Archive}
          onClick={async () => {
            ids.forEach((id) => onResolvedLocal?.(id))
            try {
              if (bulk) await bulkAction(ids, 'archive')
              else await archive(email.id)
              onRefetch?.()
            } catch {
              onRefetch?.()
            }
            close()
          }}
        >
          Archive
        </ContextMenuItem>
      )}
      <ContextMenuSeparator />
      <ContextMenuSub label="Junk & auto-trash" icon={Ban}>
        {domain && (
          <ContextMenuItem
            onClick={() => {
              close()
              void applyAutoJunk(email.id, 'sender_domain')
            }}
          >
            Whole domain
            <span className="ml-2 text-[10px] text-muted-foreground truncate">
              {domain}
            </span>
          </ContextMenuItem>
        )}
        {address && (
          <ContextMenuItem
            onClick={() => {
              close()
              void applyAutoJunk(email.id, 'sender_exact')
            }}
          >
            Just this sender
            <span className="ml-2 text-[10px] text-muted-foreground truncate">
              {address}
            </span>
          </ContextMenuItem>
        )}
      </ContextMenuSub>
      {email.status === 'binned' ? (
        <ContextMenuItem
          icon={RotateCcw}
          onClick={async () => {
            ids.forEach((id) => onResolvedLocal?.(id))
            try {
              await bulkAction(ids, 'restore')
              onRefetch?.()
            } catch {
              onRefetch?.()
            }
            close()
          }}
        >
          Restore
        </ContextMenuItem>
      ) : (
        <ContextMenuItem
          icon={Trash2}
          variant="danger"
          onClick={async () => {
            ids.forEach((id) => onResolvedLocal?.(id))
            try {
              await bulkAction(ids, 'delete')
              onRefetch?.()
            } catch {
              onRefetch?.()
            }
            close()
          }}
        >
          Bin
        </ContextMenuItem>
      )}
    </>
  )
}
